import { z } from "zod";
import { IDEA_FACT_FILTERS } from "../ideaScoreWeights.ts";
import {
  DbBool,
  EntityIdSchema,
  IsoDateTime,
  LocalIdSchema,
  PositionSchema,
  jsonColumn,
} from "./common.ts";
import {
  ResearchCategoryNameSchema,
  ResearchConfidenceSchema,
  ResearchNoteSchema,
} from "./research.ts";

export const IdeaEntityTypeSchema = z.enum([
  "track",
  "album",
  "artist",
  "playlist",
  "local_playlist",
]);
export type IdeaEntityType = z.infer<typeof IdeaEntityTypeSchema>;

export const IdeaSchema = z.object({
  id: LocalIdSchema,
  entity_type: IdeaEntityTypeSchema,
  entity_id: EntityIdSchema,
  title: z.string().min(1).max(300),
  used: DbBool,
  created_at: IsoDateTime,
  updated_at: IsoDateTime,
});
export type Idea = z.infer<typeof IdeaSchema>;

export const IdeaCardKindSchema = z.enum(["machine", "user", "research"]);

export const IdeaCardSchema = z.object({
  id: LocalIdSchema,
  idea_id: LocalIdSchema,
  kind: IdeaCardKindSchema,
  body: z.string().min(1).max(8_000),
  source_url: z.string().url().nullable(),
  edited: DbBool,
  category: z.string().nullable(),
  confidence: ResearchConfidenceSchema.nullable(),
  detail: z.string().nullable(),
  sources: jsonColumn(z.array(z.string())).nullable(),
  conflict: z.string().nullable(),
  research_ingest_id: LocalIdSchema.nullable(),
  position: PositionSchema,
  created_at: IsoDateTime,
  updated_at: IsoDateTime,
});
export type IdeaCard = z.infer<typeof IdeaCardSchema>;

export const IdeaArtistRefSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
});

export const IdeaSignalSchema = z.object({
  key: z.enum([
    "alias",
    "annotation",
    "place",
    "reissues",
    "posthumous",
    "age",
    "collaboration",
    "defunct_label",
    "samples",
    "country",
    "years",
    "label",
  ]),
  label: z.string(),
  detail: z.string().nullable(),
});
export type IdeaSignal = z.infer<typeof IdeaSignalSchema>;

/** Fixed card columns — empty slots stay blank so rows align for scanning. */
export const IdeaDisplaySlotSchema = z.enum(["born", "place", "age", "spotlight"]);
export type IdeaDisplaySlot = z.infer<typeof IdeaDisplaySlotSchema>;

export const IdeaDisplayFactSchema = z.object({
  slot: IdeaDisplaySlotSchema,
  /** One-line preview for the aligned fact row. */
  text: z.string().min(1).max(120),
  /** Marks expandable / special cells (annotation preview in spotlight). */
  kind: z.enum(["annotation"]).optional(),
  /** Full text for click-to-expand (annotation body). */
  body: z.string().min(1).max(4_000).optional(),
});
export type IdeaDisplayFact = z.infer<typeof IdeaDisplayFactSchema>;

/** Ranked row for the Ideas card list (may or may not have an idea record yet). */
export const IdeaCandidateSchema = z.object({
  entity_type: IdeaEntityTypeSchema,
  entity_id: EntityIdSchema,
  name: z.string(),
  cover_url: z.string().nullable(),
  year: z.number().int().nullable(),
  artists: z.array(IdeaArtistRefSchema).default([]),
  score: z.number(),
  /** Short distinctive blurb (≤3 unusual facts). */
  reason: z.string(),
  /** Aligned columns for the list UI. */
  display_facts: z.array(IdeaDisplayFactSchema).max(4).default([]),
  signals: z.array(IdeaSignalSchema),
  idea_id: LocalIdSchema.nullable(),
  used: DbBool,
  /** Has at least one research-provenance (pasted) card. */
  researched: z.boolean().default(false),
  /** Has compose content — manual (`user`) and/or pasted (`research`) cards. */
  has_cards: z.boolean().default(false),
});
export type IdeaCandidate = z.infer<typeof IdeaCandidateSchema>;

export const IdeaListQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  playlist_id: z.string().min(1).max(64).optional(),
  local_playlist_id: z.string().min(1).max(64).optional(),
  fact: z.enum(IDEA_FACT_FILTERS).optional(),
  used: z.enum(["unused", "used", "all"]).default("unused"),
  /**
   * Compose cards filter — filled = manual and/or pasted research.
   * Default `all` so the list still surfaces fresh MB candidates.
   */
  cards: z.enum(["with", "without", "all"]).default("all"),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});
export type IdeaListQuery = z.infer<typeof IdeaListQuerySchema>;

export const IdeaEnsureSchema = z.object({
  entity_type: IdeaEntityTypeSchema,
  entity_id: EntityIdSchema,
  title: z.string().trim().min(1).max(300).optional(),
});
export type IdeaEnsure = z.infer<typeof IdeaEnsureSchema>;

export const IdeaUpdateSchema = z
  .object({
    title: z.string().trim().min(1).max(300),
    used: z.boolean(),
  })
  .partial();
export type IdeaUpdate = z.infer<typeof IdeaUpdateSchema>;

export const IdeaCardUpdateSchema = z
  .object({
    body: z.string().trim().min(1).max(8_000),
    category: ResearchCategoryNameSchema.nullable(),
    after_id: LocalIdSchema.nullable(),
    before_id: LocalIdSchema.nullable(),
  })
  .partial();
export type IdeaCardUpdate = z.infer<typeof IdeaCardUpdateSchema>;

export const IdeaCardCreateSchema = z.object({
  body: z.string().trim().min(1).max(8_000),
  category: ResearchCategoryNameSchema.nullable().optional(),
  after_id: LocalIdSchema.nullable().optional(),
  before_id: LocalIdSchema.nullable().optional(),
});
export type IdeaCardCreate = z.infer<typeof IdeaCardCreateSchema>;

export const IdeaDetailSchema = IdeaSchema.extend({
  cover_url: z.string().nullable(),
  year: z.number().int().nullable(),
  artists: z.array(IdeaArtistRefSchema).default([]),
  subtitle: z.string().nullable(),
  cards: z.array(IdeaCardSchema).default([]),
  research_notes: z.array(ResearchNoteSchema).default([]),
});
export type IdeaDetail = z.infer<typeof IdeaDetailSchema>;
